import { useReduxState } from 'hooks';
import { ErrorPage } from 'components/error';
import { Page, PageContent, PageNav } from 'components/page';
import { CourseHeader } from '.';

import type { UserCoursePermission } from 'shared-types';
import type { PageState } from 'types';

interface Props {
  activeLink?: string;
  course: PageState['course'];
  children: React.ReactNode;
  ensureAdminPermission?: UserCoursePermission;
  ensureCourseOwner?: boolean;
  withSidebar?: boolean;
}

const CoursePage = ({
  activeLink,
  children,
  course,
  ensureAdminPermission,
  ensureCourseOwner,
  withSidebar
}: Props) => {
  const { messages } = useReduxState();

  if (!course) {
    return (
      <ErrorPage
        description={messages.errorPermissionMaybe}
        icon="book"
        title={messages.commonCourseNotFound}
      />
    );
  } else if (
    (ensureCourseOwner && !course.isOwnerOrSuperadmin) ||
    (ensureAdminPermission &&
      !course.isOwnerOrSuperadmin &&
      (!course.isAdmin ||
        !course.adminPermissions.includes(ensureAdminPermission)))
  ) {
    return (
      <ErrorPage
        description={messages.errorPermission}
        icon="book"
        title={messages.errorForbidden}
      />
    );
  }

  const {
    adminPermissions,
    chapters,
    isOwnerOrSuperadmin,
    slug,
    unreadFeedbackCount,
    unreviewedAnswerCount,
    unseenAccessRequests
  } = course;

  const links = [];

  if (isOwnerOrSuperadmin) {
    links.push({
      as: `/admin/courses/${slug}`,
      href: '/admin/courses/[courseSlug]',
      icon: 'home',
      title: messages.commonSummary
    });
  }

  if (isOwnerOrSuperadmin || adminPermissions.includes('edit-content')) {
    links.push({
      as: `/admin/courses/${slug}/edit`,
      href: '/admin/courses/[courseSlug]/edit',
      icon: 'pen',
      title: messages.pagesEdit,
      unreadItems: chapters.filter((chapter) => !chapter.publishDate).length,
      warningItems: chapters.filter((chapter) => chapter.isDraft).length
    });
  }

  if (isOwnerOrSuperadmin || adminPermissions.includes('edit-settings')) {
    links.push({
      as: `/admin/courses/${slug}/settings`,
      href: '/admin/courses/[courseSlug]/settings',
      icon: 'cog',
      title: messages.pagesSettings
    });
  }

  if (isOwnerOrSuperadmin) {
    links.push({
      as: `/admin/courses/${slug}/publish`,
      href: '/admin/courses/[courseSlug]/publish',
      icon: 'check-circle',
      title: messages.commonPublish
    });
  }

  if (isOwnerOrSuperadmin || adminPermissions.includes('manage-users')) {
    links.push({
      as: `/admin/courses/${slug}/share`,
      href: '/admin/courses/[courseSlug]/share',
      icon: 'user',
      title: messages.commonShare,
      unreadItems: unseenAccessRequests
    });
  }

  if (
    isOwnerOrSuperadmin ||
    (adminPermissions.includes('review-tasks') &&
      adminPermissions.includes('view-feedback'))
  ) {
    links.push({
      as: `/admin/courses/${slug}/follow/users`,
      href: '/admin/courses/[courseSlug]/follow/users',
      icon: 'chart-line',
      title: messages.pagesFollow,
      unreadItems: unreadFeedbackCount + unreviewedAnswerCount
    });
  } else if (adminPermissions.includes('review-tasks')) {
    links.push({
      as: `/admin/courses/${slug}/follow/users`,
      href: '/admin/courses/[courseSlug]/follow/users',
      icon: 'chart-line',
      title: messages.pagesFollow,
      unreadItems: unreviewedAnswerCount
    });
  } else if (adminPermissions.includes('view-feedback')) {
    links.push({
      as: `/admin/courses/${slug}/follow/feedback`,
      href: '/admin/courses/[courseSlug]/follow/feedback',
      icon: 'chart-line',
      title: messages.pagesFollow,
      unreadItems: unreadFeedbackCount
    });
  } else if (adminPermissions.includes('view-analytics')) {
    links.push({
      as: `/admin/courses/${slug}/follow/visitors`,
      href: '/admin/courses/[courseSlug]/follow/visitors',
      icon: 'chart-line',
      title: messages.pagesFollow
    });
  }

  if (isOwnerOrSuperadmin) {
    links.push({
      alignRight: true,
      as: `/admin/courses/${slug}/manage`,
      href: '/admin/courses/[courseSlug]/manage',
      icon: 'tools',
      title: messages.pagesManage
    });
  }

  return (
    <Page activePathname="/courses">
      <CourseHeader course={course} />
      <PageNav
        activeLink={`/admin/courses/${slug}${
          activeLink ? '/' + activeLink : ''
        }`}
        links={links}
      />
      <PageContent withSidebar={withSidebar}>{children}</PageContent>
    </Page>
  );
};

export default CoursePage;
